import React, { useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { Button, Col, Form } from 'react-bootstrap';
import { useFormikContext } from 'formik';
import { TextField } from '@components/Form/TextField';
import { getAddress } from '@services/address-client';
import styles from './Address.module.scss';

export const DeliveryAddress = () => {
  const { t } = useTranslation('register');
  const formikContext = useFormikContext<any>();
  const manualDeliveryAddress = formikContext.values['manualDeliveryAddress'] ?? '';
  const street = formikContext.values['deliveryStreet'] ?? '';
  const houseNumber = formikContext.values['deliveryHouseNumber'] ?? '';
  const houseNumberAddition = formikContext.values['deliveryHouseNumberAddition'] ?? '';
  const postalCode = formikContext.values['deliveryPostalCode'] ?? '';
  const city = formikContext.values['deliveryCity'] ?? '';

  const [shouldShowAdressBlock, setShowAddressBlock] = useState(false);
  const [isAdressApiAvailable, setAdressApiAvailable] = useState(true);
  const [isApiAdressValid, setApiAdressValid] = useState<boolean | undefined>(undefined);

  const doAddressLookup = async () => {
    if (!!postalCode && !!houseNumber) {
      try {
        const response = await getAddress({
          postalCode: postalCode,
          houseNumber: houseNumber,
          houseNumberAddition: houseNumberAddition
        });

        formikContext.setFieldValue('deliveryStreet', response?.street ?? '');
        formikContext.setFieldValue('deliveryCity', response?.city ?? '');

        setShowAddressBlock(true);
        setAdressApiAvailable(true);
        setApiAdressValid(true);
      } catch (e) {
        // TODO: error reporting
        setAdressApiAvailable(false);
        setApiAdressValid(false);
        setShowAddressBlock(false);

        formikContext.setFieldTouched('manualDeliveryAddress', true, false);
        formikContext.setFieldValue('manualDeliveryAddress', true);

        formikContext.setFieldTouched('deliveryStreet', false, false);
        formikContext.setFieldValue('deliveryStreet', '');
        formikContext.setFieldTouched('deliveryCity', false, false);
        formikContext.setFieldValue('deliveryCity', '');
      }
    }
  };

  useEffect(() => {
    const keys = ['deliveryPostalCode', 'deliveryHouseNumber', 'deliveryCity', 'deliveryStreet'];
    const isValid =
      // Has a value ( !! )
      keys.every((current) => !!formikContext.values[current]) &&
      // Not has an error
      keys.every((currentValue) => !formikContext.errors[currentValue]);

    if (isValid) {
      setShowAddressBlock(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postalCode, houseNumber, city, street]);

  useEffect(() => {
    const isTouched = formikContext.touched['deliveryPostalCode'] && formikContext.values['deliveryHouseNumber'];
    const hasValues = postalCode && houseNumber;
    const isManual = street && city;

    if (isAdressApiAvailable && isTouched && hasValues && !isManual) {
      doAddressLookup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postalCode, houseNumber, houseNumberAddition]);

  const toggleManualAddress = async () => {
    // Transitioning from manual -> automatic, `wasManual` is still `true` till next render
    const wasManual = formikContext.values['manualDeliveryAddress'];
    const touched = formikContext.touched['deliveryStreet'] || formikContext.touched['deliveryCity'];

    if (touched === true && wasManual === true) {
      await doAddressLookup();
    }
    formikContext.setFieldTouched('manualDeliveryAddress', true, false);
    formikContext.setFieldValue('manualDeliveryAddress', !formikContext.values['manualDeliveryAddress']);
  };

  return (
    <>
      <Form.Row>
        <Form.Group as={Col} xs={12} md={6}>
          <TextField label={t('label.postal-code')} type="text" name="deliveryPostalCode" />
        </Form.Group>
        <Form.Group as={Col} xs={12} md={4}>
          <TextField label={t('label.house-number')} type="number" name="deliveryHouseNumber" onBlur={doAddressLookup} min={0} />
        </Form.Group>
        <Form.Group as={Col} xs={12} md={2}>
          <TextField placeholder={t('label.house-number-addition')} type="text" name="deliveryHouseNumberAddition" />
        </Form.Group>
      </Form.Row>

      <Form.Row className={manualDeliveryAddress || isApiAdressValid === false ? '' : styles.hiddenField}>
        <Form.Group as={Col} xs={12} md={6}>
          <TextField label={t('label.street')} type="text" name="deliveryStreet" />
        </Form.Group>
        <Form.Group as={Col} xs={12} md={6}>
          <TextField label={t('label.city')} type="text" name="deliveryCity" />
        </Form.Group>
      </Form.Row>
      {shouldShowAdressBlock && (
        <Form.Group>
          <Form.Row>
            <Form.Group as={Col} xs={12} md={6}>
              <div className={`${styles.addressBlock} p-3`}>
                <span className="font-weight-bold text-white" data-testid="address">
                  {t('title.delivery-address')}
                </span>
                <br />
                {street ? (
                  <>
                    <span className="text-white street">{street} </span>
                    <span className="text-white houseNumber">
                      {houseNumber} {houseNumberAddition}
                    </span>
                    <br />
                  </>
                ) : (
                  <>
                    <span className={styles.addresLine}></span>
                    <br />
                  </>
                )}

                {postalCode ? (
                  <>
                    <span className="text-white postalCode">{postalCode}</span> <span className="text-white city">{city}</span>
                    <br />
                  </>
                ) : (
                  <>
                    <span className={styles.addresLine}></span>
                    <br />
                  </>
                )}

                <input
                  type="checkbox"
                  name="manualDeliveryAddress"
                  checked={manualDeliveryAddress}
                  className={`${styles.manualAddressSelection} ${styles.hiddenField}`}
                  readOnly
                />
                {isAdressApiAvailable && !manualDeliveryAddress && (
                  <>
                    <br />
                    <Button
                      type="button"
                      variant="link"
                      onClick={toggleManualAddress}
                      className={`${styles.toggleManualAddress} p-0`}
                      data-testid="toggleManualAddress"
                    >
                      <span className={styles.titleManualAddress}>{t('button.manual-address-selection')}</span>
                    </Button>
                  </>
                )}
              </div>
            </Form.Group>
          </Form.Row>
        </Form.Group>
      )}
    </>
  );
};
